/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 *
 * Rental-agreement step. Shows the agreement summary (renter, vehicle, dates, pricing), captures a
 * signature + consent, generates a signed PDF client-side with pdf-lib, and uploads it via
 * POST /Agreement/rental-agreement/{bookingId}/accept (Aegis.Back has no server-side PDF-from-signature).
 * Only shown when the owner requires an agreement (booking-info.requiresAgreement).
 */

import { useEffect, useRef, useState } from 'react';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { X, Loader2, Eraser } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

interface Props {
  bookingId: string;
  onClose: () => void;
  onSigned: (needsCard: boolean) => void;
}

export default function AgreementModal({ bookingId, onClose, onSigned }: Props) {
  const { t } = useTranslation();
  const [data, setData] = useState<Record<string, any> | null>(null);
  const [consent, setConsent] = useState(false);
  const [hasSig, setHasSig] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);

  useEffect(() => {
    api.getAgreementData(bookingId).then(setData).catch(() => setData(null));
  }, [bookingId]);

  // ── signature pad ──
  const pos = (e: React.MouseEvent | React.TouchEvent) => {
    const c = canvasRef.current!;
    const rect = c.getBoundingClientRect();
    const p = 'touches' in e ? e.touches[0] : (e as React.MouseEvent);
    return { x: ((p.clientX - rect.left) / rect.width) * c.width, y: ((p.clientY - rect.top) / rect.height) * c.height };
  };
  const start = (e: any) => { drawing.current = true; const ctx = canvasRef.current!.getContext('2d')!; const { x, y } = pos(e); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e: any) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current!.getContext('2d')!;
    ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.strokeStyle = '#111827';
    const { x, y } = pos(e); ctx.lineTo(x, y); ctx.stroke(); setHasSig(true);
  };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current!; c.getContext('2d')!.clearRect(0, 0, c.width, c.height); setHasSig(false); };

  const g = (...keys: string[]) => { for (const k of keys) { const v = data?.[k]; if (v != null && v !== '') return String(v); } return ''; };
  // For companies in non-English countries the backend flags PreferOriginalScript and supplies the
  // native-script name. Show native on screen (the browser renders any script); the signed PDF uses a
  // Latin-only font (pdf-lib Helvetica), so winAnsiSafe() falls back to the Latin name for scripts the
  // font can't encode (Cyrillic, Arabic, CJK, …) to avoid a crash.
  const prefersOriginal = !!(data?.preferOriginalScript ?? data?.PreferOriginalScript);
  const renterNameLatin = `${g('firstName', 'FirstName')} ${g('lastName', 'LastName')}`.trim();
  const renterNameNative = `${g('firstNameLocal', 'FirstNameLocal')} ${g('lastNameLocal', 'LastNameLocal')}`.trim();
  const renterName = prefersOriginal && renterNameNative ? renterNameNative : renterNameLatin;
  const winAnsiSafe = (s: string, fallback: string) => (/^[\x00-\xFF]*$/.test(s || '') ? s : (fallback || '').trim());
  const vehicle = `${g('carBrand', 'CarBrand')} ${g('carModel', 'CarModel')} ${g('carYear', 'CarYear')}`.trim();
  const dateStr = (k: string, k2: string) => { const v = g(k, k2); return v ? new Date(v).toLocaleString() : ''; };

  const sign = async () => {
    if (!hasSig) { setError(t('agreement.signRequired', 'Please sign to continue.')); return; }
    if (!consent) { setError(t('agreement.consentRequired', 'Please accept the terms.')); return; }
    setBusy(true); setError('');
    try {
      const pdf = await PDFDocument.create();
      const page = pdf.addPage([595, 842]);
      const font = await pdf.embedFont(StandardFonts.Helvetica);
      const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
      let y = 800;
      const line = (text: string, size = 11, b = false) => { page.drawText(text || '', { x: 50, y, size, font: b ? bold : font, color: rgb(0.1, 0.1, 0.1) }); y -= size + 8; };
      line('Rental Agreement', 18, true); y -= 6;
      line(`Booking: ${bookingId}`, 9); y -= 4;
      line(`Renter: ${winAnsiSafe(renterName, renterNameLatin)}`, 11, true);
      if (g('email', 'Email')) line(`Email: ${g('email', 'Email')}`);
      if (g('phone', 'Phone')) line(`Phone: ${g('phone', 'Phone')}`);
      if (g('driverLicense', 'DriverLicense')) line(`Driver license: ${g('driverLicense', 'DriverLicense')}`);
      y -= 4;
      line(`Vehicle: ${vehicle}`, 11, true);
      if (g('plateNumber', 'PlateNumber')) line(`Plate: ${g('plateNumber', 'PlateNumber')}`);
      y -= 4;
      line(`From: ${dateStr('dateFrom', 'DateFrom')}`);
      line(`To:   ${dateStr('dateTo', 'DateTo')}`);
      if (g('ratePerDay', 'RatePerDay')) line(`Rate: ${g('ratePerDay', 'RatePerDay')}`);
      if (g('securityDeposit', 'SecurityDeposit')) line(`Security deposit: ${g('securityDeposit', 'SecurityDeposit')}`);
      if (g('finalTotal', 'FinalTotal', 'totalCharges', 'TotalCharges')) line(`Total: ${g('finalTotal', 'FinalTotal', 'totalCharges', 'TotalCharges')}`);
      y -= 10;
      line('The renter has read and agreed to the rental terms and conditions.', 10);
      line(`Accepted at: ${new Date().toISOString()}`, 9);
      y -= 16;
      line('Signature:', 11, true);
      const sigPng = canvasRef.current!.toDataURL('image/png');
      const png = await pdf.embedPng(await (await fetch(sigPng)).arrayBuffer());
      page.drawImage(png, { x: 50, y: y - 90, width: 220, height: 90 });

      const bytes = await pdf.save();
      const blob = new Blob([bytes as unknown as BlobPart], { type: 'application/pdf' });
      const res = await api.acceptAgreement(bookingId, blob);
      if (!res.ok) { setError(res.message || t('agreement.failed', 'Could not submit the agreement.')); return; }
      onSigned(res.needsCard);
    } catch (e: any) {
      setError(e?.message || t('agreement.failed', 'Could not submit the agreement.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="dialog-card w-full max-w-lg p-6 relative max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"><X size={20} /></button>
        <h2 className="text-xl font-bold text-gray-900">{t('agreement.title', 'Rental agreement')}</h2>
        <p className="text-sm text-gray-500 mt-1 mb-4">{t('agreement.subtitle', 'Review, sign and accept to confirm your booking.')}</p>

        {!data ? (
          <div className="py-8 text-center text-gray-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
        ) : (
          <>
            <div className="text-sm bg-gray-50 rounded-lg p-4 space-y-1 text-gray-700">
              {renterName && <div><span className="text-gray-500">{t('agreement.renter', 'Renter')}:</span> {renterName}</div>}
              {vehicle && <div><span className="text-gray-500">{t('agreement.vehicle', 'Vehicle')}:</span> {vehicle}</div>}
              {dateStr('dateFrom', 'DateFrom') && <div><span className="text-gray-500">{t('agreement.from', 'From')}:</span> {dateStr('dateFrom', 'DateFrom')}</div>}
              {dateStr('dateTo', 'DateTo') && <div><span className="text-gray-500">{t('agreement.to', 'To')}:</span> {dateStr('dateTo', 'DateTo')}</div>}
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-700">{t('agreement.signature', 'Signature')}</label>
                <button onClick={clear} className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1"><Eraser size={13} /> {t('agreement.clear', 'Clear')}</button>
              </div>
              <canvas
                ref={canvasRef} width={500} height={140}
                className="w-full border border-gray-300 rounded-lg bg-white touch-none"
                onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                onTouchStart={start} onTouchMove={move} onTouchEnd={end}
              />
            </div>

            <label className="mt-3 flex items-start gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
              <span>{t('agreement.consent', 'I have read and agree to the rental terms and conditions.')}</span>
            </label>

            {error && <div className="mt-3 text-sm rounded-lg bg-red-50 text-red-700 px-3 py-2">{error}</div>}

            <button onClick={sign} disabled={busy} className="btn-primary w-full mt-4 py-2.5">
              {busy ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : t('agreement.accept', 'Sign & accept')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
